<script>
  import { binderState, updateSelectionState } from '../../lib/stores/binder.svelte.js';
  import { zoomIn } from '../../lib/stores/zoom.svelte.js';
  import { getCurrencySymbol } from '../../lib/logic/utils.js';
  import { animateValue } from '../../lib/logic/animate-price.js';
  import { formatSimpleList, copyToClipboard } from '../../lib/logic/copy-list.js';
  import { dragresize } from '../../lib/actions/dragresize.js';
  import { listswipe } from '../../lib/actions/listswipe.js';

  // --- Local state ---
  let expanded = $state(false);
  let displayPrice = $state(0);
  let copySimpleText = $state('Copy List');
  let containerEl = $state(null);
  let overlayEl = $state(null);
  let headerEl = $state(null);

  // --- Derived from binder store ---
  const hasCards = $derived(binderState.selectedCards.length > 0);
  const sortedCards = $derived([...binderState.selectedCards].sort((a, b) => b.price - a.price));
  const totalCount = $derived(
    binderState.selectedCards.reduce((sum, c) => sum + c.selectedCount, 0)
  );
  const totalPrice = $derived(
    binderState.selectedCards.reduce((sum, c) => sum + c.price * c.selectedCount, 0)
  );
  const currencySymbol = $derived(getCurrencySymbol(binderState.selectedCurrency));

  // --- Price animation ---
  // Track the "from" value outside reactivity so onFrame updates don't re-trigger the effect
  let priceAnimation = null;
  let animatingFrom = 0;
  $effect(() => {
    const target = totalPrice;
    if (priceAnimation) priceAnimation.cancel();
    const from = animatingFrom;
    priceAnimation = animateValue(from, target, (v) => {
      displayPrice = v;
      animatingFrom = v;
    });
    return () => {
      if (priceAnimation) priceAnimation.cancel();
    };
  });

  // --- Drawer max height CSS variable ---
  function updateMaxHeight() {
    if (!containerEl) return;
    const vh = window.innerHeight;
    const maxH = window.innerWidth <= 768 ? vh * 0.75 : vh * 0.5;
    containerEl.style.setProperty('--drawer-max-height', `${maxH}px`);
  }

  $effect(() => {
    if (containerEl) {
      updateMaxHeight();
    }
  });

  // Listen for keyboard-driven drawer toggle (ArrowUp/ArrowDown from BinderView)
  $effect(() => {
    function onDrawerToggle(e) {
      if (!hasCards) return;
      if (e.detail.direction === 'open') {
        expanded = true;
      } else {
        expanded = false;
      }
    }
    window.addEventListener('drawer-toggle', onDrawerToggle);
    return () => window.removeEventListener('drawer-toggle', onDrawerToggle);
  });

  // --- Handlers ---
  function toggleDrawer() {
    if (!hasCards) return;
    expanded = !expanded;
  }

  function handleStateChange(isExpanded) {
    expanded = isExpanded;
  }

  function handleTap() {
    toggleDrawer();
  }

  function handleOverlayClick() {
    expanded = false;
  }

  function handleRemoveCard(card) {
    // Find the card index in allCardsData
    const index = binderState.allCardsData.findIndex(
      (d) => d.cardData?.id === card.id && (d.isFoil || false) === (card.isFoil || false)
    );
    if (index !== -1) {
      updateSelectionState(index, 0);
    }
  }

  function handleQuantityChange(card, newQty) {
    const index = binderState.allCardsData.findIndex(
      (d) => d.cardData?.id === card.id && (d.isFoil || false) === (card.isFoil || false)
    );
    if (index !== -1) {
      updateSelectionState(index, parseInt(newQty, 10));
    }
  }

  function handleZoomCard(card) {
    // Find the card's index in allCardsData
    const index = binderState.allCardsData.findIndex(
      (d) => d.cardData?.id === card.id && (d.isFoil || false) === (card.isFoil || false)
    );
    if (index === -1) return;

    // Find the card slot element in the DOM by data-original-index
    const slotEl = /** @type {HTMLElement | null} */ (
      document.querySelector(`.card-slot[data-original-index="${index}"]`)
    );
    if (!slotEl) return;

    const cardInfo = binderState.allCardsData[index];
    expanded = false;
    // Small delay so drawer collapse animation doesn't conflict
    setTimeout(() => {
      zoomIn(cardInfo, slotEl);
    }, 50);
  }

  async function handleCopySimple() {
    const text = formatSimpleList(sortedCards, binderState.selectedCurrency);
    const ok = await copyToClipboard(text);
    if (ok) {
      copySimpleText = 'Copied!';
      setTimeout(() => {
        copySimpleText = 'Copy List';
      }, 1500);
    }
  }
</script>

<svelte:window onresize={updateMaxHeight} />

<!-- Background overlay -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="drawer-overlay"
  class:is-visible={expanded && hasCards}
  bind:this={overlayEl}
  onclick={handleOverlayClick}
></div>

<!-- Floating action bar -->
<div class="action-bar" class:is-visible={expanded && hasCards}>
  <button class="action-bar-button" onclick={handleCopySimple}>
    {copySimpleText}
  </button>
</div>

<!-- Drawer container -->
<div class="drawer-container" class:is-visible={hasCards} class:expanded bind:this={containerEl}>
  <div
    class="drawer-header"
    bind:this={headerEl}
    use:dragresize={{
      container: containerEl,
      overlay: overlayEl,
      onStateChange: handleStateChange,
      onTap: handleTap,
    }}
  >
    <h2 class="drawer-title">
      {totalCount} Card{totalCount !== 1 ? 's' : ''} Selected
    </h2>
    <div class="drawer-price">
      Total: {currencySymbol}{displayPrice.toFixed(2)}
    </div>
  </div>

  <div class="drawer-content">
    <ul class="card-list">
      {#each sortedCards as card (card.id + '_' + (card.isFoil || false))}
        <li
          class="swipeable"
          use:listswipe={{
            onSwipeLeft: () => handleRemoveCard(card),
            onSwipeRight: null,
            leftColor: '#ef4444',
          }}
        >
          <div class="swipe-bg swipe-bg-left">
            <span class="swipe-icon">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="white"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                ><polyline points="3 6 5 6 21 6"></polyline><path
                  d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                ></path></svg
              >
            </span>
          </div>
          <div class="swipe-content">
            <div class="card-details">
              <div class="card-name-container">
                <span class="card-name">
                  {card.name}
                  {#if card.isFoil}
                    <span class="foil-indicator"></span>
                  {/if}
                </span>
                <span class="card-set-info">
                  <span class="set-code-text">{card.set ? card.set.toUpperCase() : ''}</span>
                  {#if card.collector_number}
                    <span class="set-info-text">#{card.collector_number}</span>
                  {/if}
                  {#if card.lang && card.lang.toLowerCase() !== 'en'}
                    <span class="lang-badge">{card.lang.toUpperCase()}</span>
                  {/if}
                </span>
              </div>
            </div>

            <div class="list-action-buttons">
              <button
                class="list-action-btn remove-btn"
                title="Remove"
                onclick={(e) => {
                  e.stopPropagation();
                  handleRemoveCard(card);
                }}
              >
                <svg viewBox="0 0 24 24"
                  ><polyline points="3 6 5 6 21 6"></polyline><path
                    d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                  ></path></svg
                >
              </button>
              <button
                class="list-action-btn zoom-btn"
                title="View Card"
                onclick={(e) => {
                  e.stopPropagation();
                  handleZoomCard(card);
                }}
              >
                <svg viewBox="0 0 24 24"
                  ><circle cx="11" cy="11" r="8"></circle><line
                    x1="21"
                    y1="21"
                    x2="16.65"
                    y2="16.65"
                  ></line><line x1="11" y1="8" x2="11" y2="14"></line><line
                    x1="8"
                    y1="11"
                    x2="14"
                    y2="11"
                  ></line></svg
                >
              </button>
            </div>

            <div class="list-quantity-controls">
              {#if card.availableCount > 1}
                <select
                  class="list-quantity-select"
                  value={card.selectedCount}
                  onchange={(e) => handleQuantityChange(card, e.currentTarget.value)}
                >
                  {#each Array.from({ length: card.availableCount }, (_, i) => i + 1) as n (n)}
                    <option value={n}>{n}</option>
                  {/each}
                </select>
              {:else}
                <span class="quantity-single">1</span>
              {/if}
            </div>

            <span class="card-price">
              {currencySymbol}{(card.price * card.selectedCount).toFixed(2)}
            </span>
          </div>
        </li>
      {/each}
    </ul>
  </div>
</div>

<style>
  /* Overlay */
  .drawer-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    z-index: 49;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.3s ease-in-out,
      visibility 0s linear 0.3s;
    pointer-events: none;
  }
  .drawer-overlay.is-visible {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transition-delay: 0s, 0s;
  }

  /* Floating action bar */
  .action-bar {
    position: fixed;
    bottom: 0.5rem;
    left: 50%;
    transform: translateX(-50%) translateY(100%);
    z-index: 51;
    display: flex;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: white;
    border-radius: 2rem;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.2s,
      visibility 0.2s,
      transform 0.3s ease-in-out;
  }
  .action-bar.is-visible {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }
  .action-bar-button {
    padding: 0.4rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 500;
    border-radius: 1rem;
    border: none;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 0.15s;
    background-color: #3b82f6;
    color: white;
  }
  .action-bar-button:hover {
    background-color: #2563eb;
  }

  /* Drawer container */
  .drawer-container {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 50;
    background-color: #fff;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.15);
    height: 55px;
    max-width: 100%;
    margin: 0 auto;
    overflow: hidden;
    translate: 0 100%;
    visibility: hidden;
    transition:
      translate 0.3s ease-in-out,
      height 0.3s ease-in-out,
      visibility 0.3s;
    transition-behavior: allow-discrete;
  }
  .drawer-container.is-visible {
    translate: 0 0;
    visibility: visible;
  }
  @starting-style {
    .drawer-container.is-visible {
      translate: 0 100%;
    }
  }
  .drawer-container.expanded {
    height: var(--drawer-max-height, 50vh);
    overflow-y: auto;
  }
  @media (max-width: 768px) {
    .drawer-container.expanded {
      height: var(--drawer-max-height, 75vh);
    }
  }
  .drawer-container :global(.dragging) {
    transition: none;
    overflow-y: auto;
  }

  /* Header */
  .drawer-header {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1.5rem;
    cursor: pointer;
    border-bottom: 1px solid #e5e7eb;
    height: 55px;
    touch-action: none;
    user-select: none;
    box-sizing: border-box;
  }
  /* Drag handle indicator (mobile only) */
  .drawer-header::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 4px;
    background: #d1d5db;
    border-radius: 2px;
  }
  @media (pointer: fine) {
    .drawer-header::before {
      display: none;
    }
  }
  .drawer-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
  }
  .drawer-price {
    font-size: 0.9rem;
    font-weight: 600;
    color: #1f2937;
  }

  /* Content area */
  .drawer-content {
    padding: 0;
    overflow-y: auto;
    flex: 1;
  }

  /* Card list */
  .card-list {
    list-style: none;
    margin: 0;
    padding: 0 1rem;
    padding-bottom: 3rem;
  }
  .card-list li {
    position: relative;
    overflow: hidden;
    font-size: 0.8rem;
    min-height: 44px;
    background-color: white;
  }
  .card-list li:last-child {
    border-bottom: none;
  }

  /* Swipe structure */
  .swipeable {
    position: relative;
    overflow: hidden;
  }
  .swipe-bg {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 100%;
    display: none;
    align-items: center;
    z-index: 0;
  }
  .swipe-bg-left {
    right: 0;
    justify-content: flex-end;
    padding-right: 1.25rem;
    background-color: #ef4444;
  }
  .swipe-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .swipe-content {
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.4rem 0.5rem;
    border-bottom: 1px solid #e5e7eb;
    gap: 0.25rem;
    background-color: white;
    min-height: 44px;
  }

  /* Card details */
  .card-details {
    display: flex;
    align-items: center;
    flex-grow: 1;
    margin-right: 0.25rem;
    overflow: hidden;
  }
  .card-name-container {
    flex-grow: 1;
    margin-right: 0.5rem;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }
  .card-name {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .card-set-info {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.75em;
    color: #6b7280;
  }
  .set-info-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .set-code-text {
    color: #6b7280;
    font-size: 0.9em;
  }
  .lang-badge {
    font-size: 0.7em;
    padding: 0.1em 0.3em;
    background: #e5e7eb;
    border-radius: 3px;
    color: #4b5563;
  }

  /* Foil indicator */
  .foil-indicator {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--foil-gradient);
    margin-left: 0.15rem;
    flex-shrink: 0;
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.2);
    vertical-align: middle;
    transform: translateY(-0.05em);
  }

  /* Quantity controls */
  .list-quantity-controls {
    display: flex;
    align-items: center;
    margin-right: 0.25rem;
    min-width: 45px;
    justify-content: center;
  }
  .list-quantity-select {
    padding: 2px 4px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 0.75rem;
    background-color: white;
    cursor: pointer;
    min-width: 45px;
  }
  .list-quantity-select:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }
  .quantity-single {
    font-size: 0.75rem;
    color: #6b7280;
  }

  /* Action buttons (hover-revealed on desktop) */
  .list-action-buttons {
    display: none;
    flex-direction: row;
    align-items: center;
    flex-shrink: 0;
    margin-right: 8px;
  }
  @media (hover: hover) and (pointer: fine) {
    .swipe-content:hover .list-action-buttons {
      display: flex;
    }
  }
  .list-action-btn {
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background-color: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.15s;
  }
  .list-action-btn :global(svg) {
    width: 16px;
    height: 16px;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    fill: none;
  }
  .list-action-btn.remove-btn :global(svg) {
    stroke: #ef4444;
  }
  .list-action-btn.remove-btn:hover {
    background-color: rgba(239, 68, 68, 0.15);
  }
  .list-action-btn.zoom-btn :global(svg) {
    stroke: #8b5cf6;
  }
  .list-action-btn.zoom-btn:hover {
    background-color: rgba(139, 92, 246, 0.15);
  }

  /* Card price */
  .card-price {
    font-weight: 500;
    min-width: 45px;
    text-align: right;
  }
</style>
